import { head, uniq } from 'lodash-es';
import { createSelector } from 'reselect';
import type { DashboardWorkspaceType, QuoteState } from 'state/quotes/infos/infos.model';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceType } from 'state/workspaces/model';
import { buildMap } from 'utils/collection';

const _getAllStaticTabs = createSelector(
  [(state: AppState) => state.workspaces.static, (state: AppState) => state.static.infos],
  (tabMap, infosMap) => {
    return Object.keys(tabMap).map(tabId => {
      const tab = tabMap[tabId]!;
      const infos = infosMap[tabId];
      return {
        tabId,
        quoteId: tab.quoteId,
        quoteState: infos?.quoteState,
        workspace: 'static',
        solveMode: 'infos?.solveMode',
        remunerationMode: infos?.remunerationMode,
        fixedAmountInPercent: '0',
      } as TileTab;
    });
  },
);

const _getAllDynamicTabs = createSelector(
  [(state: AppState) => state.workspaces.dynamic, (state: AppState) => state.dynamic.infos],
  (tabMap, infosMap) => {
    return Object.keys(tabMap).map(tabId => {
      const tab = tabMap[tabId]!;
      const infos = infosMap[tabId];
      return {
        tabId,
        quoteId: tab.quoteId,
        quoteState: infos?.quoteState,
        workspace: 'dynamic',
        solveMode: 'string',
        remunerationMode: infos?.remunerationMode,
        fixedAmountInPercent: '0',
      } as TileTab;
    });
  },
);

function getWorkspacePriority(tab: TileTab) {
  switch (tab.workspace) {
    case 'static':
      return 1;
    case 'dynamic':
      return 0;
  }
}

function getStatusPriority(tab: TileTab) {
  switch (tab.quoteState) {
    case 'Inconsistent':
      return -1000;
    case 'Previous':
      return 0;
    case 'Live':
      return 10;
  }
}

function getPriority(tab: TileTab) {
  return getWorkspacePriority(tab) + getStatusPriority(tab);
}

export const _getTabsByQuoteId = createSelector([_getAllStaticTabs, _getAllDynamicTabs], (staticTabs, dynamicTabs) => {
  const tabs = [...staticTabs, ...dynamicTabs];
  const quoteIds = uniq(tabs.map(t => t.quoteId));
  return buildMap(quoteIds, quoteId => tabs.filter(t => t.quoteId === quoteId));
});

export type TileTab = {
  tabId: string;
  quoteId: string;
  quoteState: QuoteState;
  workspace: DashboardWorkspaceType;
  solveMode: string;
  remunerationMode: string;
  fixedAmountInPercent: string;
};

export function getTabFromQuoteId(state: AppState, quoteId: string) {
  const tabs = _getTabsByQuoteId(state).get(quoteId) ?? [];
  const sortedTabs = tabs.filter(t => t.quoteState !== 'Inconsistent').sort((x, y) => getPriority(y) - getPriority(x));
  return head(sortedTabs);
}

export function getTabFromQuoteIdAndWorkspace(state: AppState, quoteId: string, workspace: QuoteWorkspaceType) {
  const tabs = _getTabsByQuoteId(state).get(quoteId) ?? [];
  return tabs.find(t => t.workspace === workspace);
}
